import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { autorun } from 'mobx';

import useComponentMounted from '../../../hooks/useComponentMounted';
import UserContext from '../../../context/UserContext';
import UserActionItem, { ActionItemStatus } from '../../Model/UserActionItem';
import User, { UserRole } from '../../../Model/User';
import ActionItemsContext, { initialValues } from './ActionItemsContext';

const ActionItemsContextProvider = ({
  children,
}) => {
  const [isReady, setIsReady] = useState(initialValues.isReady);
  const [actionItems, setActionItems] = useState(initialValues.actionItems);
  const [unreadCount, setUnreadCount] = useState(initialValues.unreadCount);
  const [collection, setCollection] = useState(null);
  const [internalUsers, setInternalUsers] = useState(initialValues.internalUsers);
  const internalUsersLoadedRef = useRef(false);

  const isComponentMountedRef = useComponentMounted();
  const { userDoc: { id: userId } } = useContext(UserContext);

  useEffect(() => {
    const init = async () => {
      const itemsCollection = await UserActionItem.getItemsForUser(userId);

      if (isComponentMountedRef.current) {
        setCollection(itemsCollection);
        setIsReady(true);
      }
    };

    if (userId) {
      init();
    }
  }, [userId, isComponentMountedRef]);

  useEffect(() => {
    if (!collection) return () => {};

    const disposer = autorun(() => {
      const docs = collection.docs.slice();
      setActionItems(docs);
      setUnreadCount(docs.filter((item) => item.status === ActionItemStatus.PENDING).length);
    });

    return () => disposer();
  }, [collection]);

  const markAsRead = useCallback(async (itemId) => {
    const item = actionItems.find((i) => i.id === itemId);
    if (item) {
      await item.markAsRead();
    }
  }, [actionItems]);

  const resolveItem = useCallback(async (itemId) => {
    const item = actionItems.find((i) => i.id === itemId);
    if (item) {
      await item.resolve();
    }
  }, [actionItems]);

  const loadInternalUsers = useCallback(async () => {
    if (internalUsersLoadedRef.current) return;
    internalUsersLoadedRef.current = true;
    try {
      const [sdrCollection, isCollection] = await Promise.all([
        User.getUsersByRole(UserRole.SDR),
        User.getUsersByRole(UserRole.IS),
      ]);

      const seen = new Set();
      const users = [];

      [...sdrCollection.docs, ...isCollection.docs].forEach((user) => {
        if (!seen.has(user.id)) {
          seen.add(user.id);
          const normalizedFirstName = (user.firstName || '').replace(/\s+/g, '');
          const normalizedLastName = (user.lastName || '').replace(/\s+/g, '');
          users.push({
            id: user.id,
            name: `${normalizedFirstName}${normalizedLastName}`.toLowerCase(),
            displayName: user.name,
          });
        }
      });

      if (isComponentMountedRef.current) {
        setInternalUsers(users);
      }
    } catch {
      internalUsersLoadedRef.current = false; // allow retry on next @
    }
  }, [isComponentMountedRef]);

  const context = useMemo(() => ({
    actionItems,
    unreadCount,
    isReady,
    markAsRead,
    resolveItem,
    internalUsers,
    loadInternalUsers,
  }), [
    actionItems,
    unreadCount,
    isReady,
    markAsRead,
    resolveItem,
    internalUsers,
    loadInternalUsers,
  ]);

  return (
    <ActionItemsContext.Provider value={context}>
      {children}
    </ActionItemsContext.Provider>
  );
};

ActionItemsContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(ActionItemsContextProvider);
